/**
 * Desktop feature slider for the login page (right panel).
 * Auto-cycles through 3 product highlights every 4 seconds.
 */

import { Button } from '@socio-legal/react-components';

import { useTranslate } from 'hooks/useTranslate';

import {
  generateLoginSlides,
  LOGIN_SLIDER_DESKTOP_FADE_MS,
  LOGIN_SLIDER_INTERVAL_MS,
  LOGIN_SLIDER_LOGO_DARK,
  LOGIN_SLIDER_LOGO_LIGHT,
  LOGIN_SLIDER_VISUALS,
  LoginSliderProps,
  useLoginSliderCycle,
} from 'modules/auth/pages/Login/loginSliderShared';

export type FeatureSliderProps = LoginSliderProps;

export default function LoginSlider({ dark = false }: LoginSliderProps) {
  const { t } = useTranslate();
  const slides = generateLoginSlides(t);
  const { idx, visible, animKey, go } = useLoginSliderCycle({
    slideCount: slides.length,
    intervalMs: LOGIN_SLIDER_INTERVAL_MS,
    fadeMs: LOGIN_SLIDER_DESKTOP_FADE_MS,
  });

  const slide = slides[idx];
  const VisualComponent = LOGIN_SLIDER_VISUALS[slide.key];

  const panelBg = dark
    ? 'linear-gradient(160deg, #1a1e4a 0%, #0d1120 100%)'
    : 'linear-gradient(160deg, #eef0ff 0%, #f7f9ff 100%)';
  const gridClr = dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
  const titleClr = dark ? '#fff' : '#111729';
  const bodyClr = dark ? 'rgba(255,255,255,0.6)' : '#4a5568';
  const inactDot = dark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.18)';
  const logoSrc = dark ? LOGIN_SLIDER_LOGO_LIGHT : LOGIN_SLIDER_LOGO_DARK;

  return (
    <div
      style={{
        flex: 1,
        background: panelBg,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '56px 52px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <svg
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        <defs>
          <pattern
            id="sl-grid"
            x="0"
            y="0"
            width="48"
            height="48"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M48 0H0v48"
              fill="none"
              stroke={gridClr}
              strokeWidth="0.8"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#sl-grid)" />
      </svg>

      <div
        style={{
          position: 'absolute',
          top: -80,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 400,
          height: 400,
          borderRadius: '50%',
          pointerEvents: 'none',
          background: `radial-gradient(ellipse, ${slide.color}22 0%, transparent 70%)`,
          transition: 'background 0.6s ease',
        }}
      />

      <div
        key={animKey}
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 420,
          textAlign: 'center',
          transition: 'opacity 0.18s ease',
          opacity: visible ? 1 : 0,
        }}
      >
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: 16,
            background: `${slide.color}22`,
            border: `1px solid ${slide.color}44`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: `0 0 24px ${slide.color}44`,
          }}
        >
          <slide.SlideIcon size={26} color={slide.color} strokeWidth={1.8} />
        </div>

        <h2
          style={{
            margin: '0 0 12px',
            fontFamily: 'var(--sl-font-heading)',
            fontWeight: 700,
            fontSize: 'var(--sl-font-size-5xl)',
            color: titleClr,
            letterSpacing: '-0.01em',
            lineHeight: 1.15,
            whiteSpace: 'nowrap',
          }}
        >
          {slide.title}
        </h2>
        <p
          style={{
            margin: '0 0 32px',
            fontSize: 'var(--sl-font-size-md)',
            color: bodyClr,
            lineHeight: 1.6,
            maxWidth: 340,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          {slide.body}
        </p>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: 36,
          }}
        >
          <VisualComponent t={t} dark={dark} />
        </div>
      </div>

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          gap: 10,
          alignItems: 'center',
        }}
      >
        {slides.map((slideItem, i) => (
          <Button
            key={`slide-${slideItem.key}`}
            type="button"
            variant="link"
            onClick={() => go(i)}
            aria-label={t('LoginGoToSlide', { number: i + 1 })}
            style={{
              padding: 0,
              minWidth: 0,
              border: 'none',
              cursor: 'pointer',
              width: i === idx ? 24 : 8,
              height: 8,
              borderRadius: i === idx ? 4 : '50%',
              background: i === idx ? slide.color : inactDot,
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </div>

      <img
        src={logoSrc}
        alt="Sttok"
        style={{
          position: 'absolute',
          bottom: 28,
          right: 32,
          height: 20,
          opacity: 0.35,
        }}
      />
    </div>
  );
}
